import React, {useState} from 'react'
import 'react-datepicker/dist/react-datepicker.css'
import {ActionButton, FormGroup, FormGroups, FormText, NumericInput, useAlert} from "./components/FormComps"
import {useIntlEx} from "./components/IntlUtils.js"
import {useSolv} from "./components/SolvProvider";
import {closeDialog, ModalDialog} from "./components/DialogUtils";
import {mkHref} from "./components/AccountUtils";
import {useCreditTransactionTypeOpts} from "./components/CreditTransactionUtils";
import {default as Select} from "react-select";
import getLogger from "./components/Logging.js"
import './App.css'
import {InfoBlock, InfoText} from "./components/ValidationUtils";

const log = getLogger("CreditsAdjustDialog")

export default function CreditTransactionAdjustDialog(props)  {

  const { api, session, setBusy } = useSolv()
  const { intl } = useIntlEx()

  const [alert, setAlert] = useAlert()

  const {creditTransactionTypeOpts, findCreditTransactionTypeOpt} = useCreditTransactionTypeOpts(c => {
    if (["SYSTEM"].includes(session?.user?.userTypeId)) {
      switch (c.value) {
        case "BONUS":
        case "DEPOSIT":
        case "REWARD":
          return ({...c, text: `Add as ${c.text}`, sign: "+",})
        case "CORRECTION":
        case "PENALTY":
        case "REFUND":
        case "SERVICE":
          return ({...c, text: `Deduct as ${c.text}`, sign: "-",})
        case "PURCHASE_PENDING":
          return ({...c})
        default:
          return null
      }
    }
    else if (["RESELLER"].includes(session?.user?.userTypeId)) {
      switch (c.value) {
        case "DEPOSIT":
        case "PURCHASE_PENDING":
          return ({...c})
        default:
          return null
      }
    }
    else {
      return null
    }
  })

  const [credits, setCredits] = useState(0.00)
  const [description, setDescription] = useState(null)
  const [creditTransactionType, setCreditTransactionType] = useState(findCreditTransactionTypeOpt("BONUS"))
  const [touched, setTouched] = useState(null)

  function handleAdjustClick() {
    let c = parseFloat(`${creditTransactionType.sign || "+" }${credits}`)
    setBusy(intl.msg("working"))
    const payload = {
      tenantId: props.tenant.tenantId,
      creditTransactionType: creditTransactionType.value,
      paymentMethod: (creditTransactionType.value !== "PURCHASE_PENDING" ? "CREDITS" : "BANK"),
      credits: c,
      description: (creditTransactionType.value !== "PURCHASE_PENDING" ? description : intl.msg("credit_transaction_description_register_purchase_credits")),
      details: {},
    }
    api.adjustCreditTransaction(props.tenant.tenantId, payload)
      .then((data) => {
        window.location = mkHref({suffix: `/credits`, tenant: props.tenant})
      })
      .catch((error) => {
        handleError(error)
      })
      .finally(() => {
        setBusy(null)
      })
  }

  function handleError(error) {
    if (error && error.code) {
      // log.debug("handleError: error=", error.code)
      const code = error.code
      switch (code) {
        case "DUPLICATE_KEY":
          setAlert({type: "error", text: intl.msg("credit_transaction_adjust_error_duplicate")})
          break
        case "INSUFFICIENT_FUNDS":
          setAlert({type: "error", text: intl.msg("credit_transaction_adjust_error_insufficient_funds")})
          break
        default:
          setAlert({type: "error", text: intl.msg("error_failed")})
          break
      }
    }
    else {
      setAlert({type: "error", text: intl.msg("error_failed")})
    }
  }

  function handleCreditTransactionTypeChange(v) {
    setCreditTransactionType(v)
    setTouched(true)
  }

  function handleCreditsChange(v) {
    setCredits(v)
    setTouched(true)
  }

  function handleDescriptionChange(e) {
    setDescription(e.target.value)
    setTouched(true)
  }

  function handleOpenDialog() {
    setAlert(null)
  }

  function handleCloseDialog() {
    closeDialog("dlg_credits_adjust")
  }


  return (

    props.tenant &&
      <ModalDialog
        dialogId="dlg_credits_adjust"
        size="md" keyboard={"true"} backdrop={props.backdrop || "static"}
        onOpen={handleOpenDialog}
        onClose={handleCloseDialog}
      >

        <ModalDialog.Header
          title={intl.msg("credit_transaction_adjust_title")}
          description={intl.msg("credit_transaction_adjust_subtitle")}
          alert={alert}
        />

        <ModalDialog.Body>

          <FormText className="p-0 pb-3">
            {intl.msg("credit_transaction_adjust_text")}
          </FormText>

          <FormGroups>

            <FormGroup>
              <FormGroup.Label htmlFor="inp_type" text={intl.msg("credit_transaction_adjust_type")} description={intl.msg("credit_transaction_adjust_type_description")}/>
              <FormGroup.Controls>
                <Select
                  className="react-select"
                  classNamePrefix="react-select"
                  value={creditTransactionType}
                  options={creditTransactionTypeOpts}
                  isSearchable={false}
                  // menuPortalTarget={document.body}
                  // hideSelectedOptions={true}
                  onChange={handleCreditTransactionTypeChange}
                />
                <InfoBlock>
                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>

            <FormGroup>
              <FormGroup.Label htmlFor="inp_credits" text={intl.msg("credit_transaction_adjust_credits")} description={intl.msg("credit_transaction_adjust_credits_description")}/>
              <FormGroup.Controls>
                <div className="form-control-wrapper">
                  {
                    creditTransactionType?.sign &&
                      <div className="form-control-prefix">{creditTransactionType?.sign}</div>
                  }
                  <div style={{width: "120px"}}>
                    <NumericInput id="inp_credits" className="form-control" min={1} max={9999999.99} decimals={2} disabled={false} value={credits} onChange={handleCreditsChange}/>
                  </div>
                  {/*<div className="form-control-suffix">{props.currencyCode ? props.currencyCode : ""}</div>*/}
                </div>
                <InfoBlock>
                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>

            {
              creditTransactionType?.value !== "PURCHASE_PENDING" &&
                <FormGroup>
                  <FormGroup.Label htmlFor="inp_description" text={intl.msg("credit_transaction_adjust_description")} description={intl.msg("credit_transaction_adjust_description_description")}/>
                  <FormGroup.Controls>
                    <input id="inp_description" name="description" type="text" className="form-control" value={description} disabled={false} onChange={handleDescriptionChange} autoComplete="off"/>
                    <InfoBlock>
                      <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                    </InfoBlock>
                  </FormGroup.Controls>
                </FormGroup>
            }

          </FormGroups>

        </ModalDialog.Body>

        <ModalDialog.Footer>
          <ModalDialog.Footer.Controls>
            <ActionButton name="cancel" onClick={handleCloseDialog}/>
            <ActionButton msg="adjust" icon="ok" flavor="primary" disabled={alert || (!credits || credits <= 0.0)} onClick={handleAdjustClick}/>
          </ModalDialog.Footer.Controls>
        </ModalDialog.Footer>

      </ModalDialog>
  )
}